import type { ReactElement } from "react";
import { useId } from "react";

import { useSelector } from "react-redux";

import { DropdownButton } from "@similarweb/ui-components";

import HooksModalV2Key from "app/constants/HooksModalV2Key";
import { translate } from "app/filters/i18nFilter";
import { CountrySelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/CountrySelectCombobox";
import { FormFieldLabel } from "app/pages/sales-intelligence/common-components/form-fields";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { selectIsVisitsFromWorldwide } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { StyledDropdownButtonWrapper } from "app/pages/sales-intelligence/modules/leads-search-filters/components/CountryDropdownButton/styles";
import {
    FilterContentContainer,
    StyledCountryDropdownContainer,
} from "app/pages/sales-intelligence/modules/leads-search-filters/components/filters/styles";
import { RangeSelectWithClearField } from "app/pages/sales-intelligence/modules/leads-search-filters/components/RangeSelectWithClearField/RangeSelectWithClearField";
import RangeSlider from "app/pages/sales-intelligence/modules/leads-search-filters/components/RangeSlider/RangeSlider";
import { useInjectedVisitsFromWorldwide } from "app/pages/sales-intelligence/modules/leads-search-filters/context/VisitsFromWorldwideContext";
import type FindInternationalVisitsFilter from "app/pages/sales-intelligence/modules/leads-search-filters/models/InternationalVisitsFilter/InternationalVisitsFilter";
import type { IInternationalVisitsFilterDto } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import { formatNumber } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/numberFormatters";
import { openHooksModalV5 } from "app/services/ModalService";

import { COUNTRY_SELECT_WIDTH } from "../../../constants/index";

import { TEXT_KEYS } from "./const";
import { StyledVisitsContent } from "./styles";
import { useInternationalVisitsFilter } from "./useInternationalVisitsFilter";
import { wrapWithTooltip } from "./wrapWithTooltip";

const InternationalVisitsFilter = ({
    filter: {
        options: { buckets, countriesOptions },
    },
    value,
    onFilterUpdate,
}: IFilterViewBaseProps<FindInternationalVisitsFilter>): ReactElement | null => {
    const dropdownId = useId();

    const {
        isCountrySelected,
        handleCountrySelect,
        handleVisitsClear,
        handleVisitsChange,
        handleInternationalVisitsAfterChange,
    } = useInternationalVisitsFilter({ value, onFilterUpdate });

    const injectedVisitsFromWorldwide = useInjectedVisitsFromWorldwide();
    const reduxVisitsFromWorldwide = useSelector(selectIsVisitsFromWorldwide);
    const isVisitsFromWorldwide = injectedVisitsFromWorldwide ?? reduxVisitsFromWorldwide;

    const renderCountryDropdownButton = () => (
        // Inlined so Combobox.Trigger's `asChild` can merge its ref onto this styled.div.
        <StyledDropdownButtonWrapper disabled={!isVisitsFromWorldwide}>
            <DropdownButton disabled={!isVisitsFromWorldwide}>{translate(TEXT_KEYS.DROPDOWN_BUTTON)}</DropdownButton>
        </StyledDropdownButtonWrapper>
    );

    const visitsFiltersContent = (
        <StyledVisitsContent>
            <RangeSelectWithClearField<IInternationalVisitsFilterDto>
                field="internationalVisits"
                value={value?.internationalVisits}
                buckets={buckets.internationalVisits}
                formatter={formatNumber}
                onHandleChange={(_, range) => handleVisitsChange(range)}
                onHandleClear={handleVisitsClear}
                isDisabled={!isCountrySelected || !isVisitsFromWorldwide}
            />
            <RangeSlider
                name="internationalVisitsShare"
                title={translate(leadsSearchFiltersI18nKeys.filter("internationalVisitsShare"))}
                options={buckets.internationalVisitsShare}
                value={value?.internationalVisitsShare}
                onAfterChange={handleInternationalVisitsAfterChange}
                isDisabled={!isCountrySelected || !isVisitsFromWorldwide}
            />
        </StyledVisitsContent>
    );

    const internationalVisitsFilterView = (
        <FilterContentContainer>
            <FormFieldLabel text={translate(leadsSearchFiltersI18nKeys.filter("internationalCountry"))}>
                <StyledCountryDropdownContainer
                    id={dropdownId}
                    customWidth={COUNTRY_SELECT_WIDTH}
                >
                    <CountrySelectCombobox
                        onSelect={handleCountrySelect}
                        onRestrictedClick={() => {
                            openHooksModalV5(HooksModalV2Key.WebCountry);
                            return true;
                        }}
                        selectedIds={value?.internationalCountry ? [value.internationalCountry] : []}
                        items={countriesOptions}
                        renderButton={renderCountryDropdownButton}
                        searchPlaceHolder={translate(TEXT_KEYS.DROPDOWN_PLACEHOLDER)}
                        disabled={!isVisitsFromWorldwide}
                    />
                </StyledCountryDropdownContainer>
            </FormFieldLabel>
            {wrapWithTooltip({
                content: visitsFiltersContent,
                tooltipText: translate(TEXT_KEYS.TOOLTIP_DISABLED_VISITS_SECTION),
                shouldWrap: !isCountrySelected && isVisitsFromWorldwide,
            })}
        </FilterContentContainer>
    );

    return wrapWithTooltip({
        content: internationalVisitsFilterView,
        tooltipText: translate("si.lead_gen_filters.group.international_visits.tooltip.disabled"),
        shouldWrap: !isVisitsFromWorldwide,
    });
};

export default InternationalVisitsFilter;
